import { Table, Pagination, UnstyledButton, Group, Text, Popover, ActionIcon, MultiSelect, Select, Paper, Badge, Box } from "@mantine/core";
import React, { useState, useEffect, memo } from "react";
import { IconChevronUp, IconChevronDown, IconSelector, IconFilter, IconTable, IconList } from '@tabler/icons-react';
import type { ReactNode} from "react";
import classes from './styles.module.css';
import { useTranslation } from "../../hooks/useTranslation";
import useTransientStore from "../../data/transcientStore";

export interface ColumnFilter<T> {
  type: 'multiselect' | 'text';
  options?: (data: T[]) => { value: string; label: string }[];
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
}

export interface ColumnDef<T> {
  header: string | ReactNode;
  accessor: keyof T | string;
  sortable?: boolean;
  render?: (row: T) => ReactNode;
  width?: string | number;
  filter?: ColumnFilter<T>;
}

interface SortableTableProps<T> {
  data: T[];
  columns: ColumnDef<T>[];
  pageSize?: number;
  rowKey: (row: T) => string | number;
  actionColumns?: { label?: string; render: (row: T) => React.ReactNode; width?: string | number }[];
  showPagination?: boolean;
  stickyHeaderOffset?: number;
  rowStyle?: (row: T) => React.CSSProperties;
  filterRow?: React.ReactNode;
  title?: string;
  pageSizeOptions?: number[];
}

function getValueByPath(obj: any, path: string) {
  return path.split('.').reduce((acc, part) => (acc && acc[part] !== undefined ? acc[part] : undefined), obj);
}

interface TableRowProps<T> {
  row: T;
  rowKey: string | number;
  columns: ColumnDef<T>[];
  actionColumns?: { label?: string; render: (row: T) => React.ReactNode; width?: string | number }[];
  rowStyle?: React.CSSProperties;
  getValueByPath: (obj: any, path: string) => any;
}

function TableRowInner<T>({
  row,
  rowKey,
  columns,
  actionColumns,
  rowStyle,
  getValueByPath,
}: TableRowProps<T>) {
  return (
    <Table.Tr style={rowStyle}>
      {columns.map((col) => {
        const value = col.render ? col.render(row) : getValueByPath(row, col.accessor as string);
        const isEmpty = value == null || value === '';
        return (
          <Table.Td 
            key={col.accessor as string}
            style={{ width: col.width }}
          >
            <Text size="sm">
              {isEmpty ? '-' : value}
            </Text>
          </Table.Td>
        );
      })}
      {actionColumns && actionColumns.map((action, idx) => (
        <Table.Td 
          key={idx} 
          className={classes.centerAlign}
          style={{ width: action.width }}
        >
          {action.render(row)}
        </Table.Td>
      ))}
    </Table.Tr>
  );
}

const MemoizedTableRow = memo(TableRowInner, (prevProps, nextProps) => {
  return prevProps.row === nextProps.row && 
         prevProps.columns === nextProps.columns &&
         prevProps.actionColumns === nextProps.actionColumns;
}) as typeof TableRowInner;

export function SortableTable<T>({
  data,
  columns,
  pageSize = 100,
  rowKey,
  actionColumns,
  showPagination = true,
  stickyHeaderOffset = 60,
  rowStyle,
  filterRow,
  title,
  pageSizeOptions = [5, 10, 25, 50, 100],
}: SortableTableProps<T>) {
  const selectedAddressSystem = useTransientStore((state) => state.selectedAddressSystem);
  const addrIdFromStore = selectedAddressSystem?.id || 0;

  const { getTranslation } = useTranslation({ pageId: 322, addressSystemId: addrIdFromStore || 0, autoLoad: true });
  const [activePage, setPage] = useState(1);
  const [currentPageSize, setCurrentPageSize] = useState(pageSize);
  const [sortColumn, setSortColumn] = useState<string>("");
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [filterPopovers, setFilterPopovers] = useState<Record<string, boolean>>({});

  useEffect(() => {
    setPage(1);
  }, [data, currentPageSize]);

  const handlePageSizeChange = (newPageSize: string | null) => {
    if (newPageSize) {
      const size = newPageSize === 'all' ? sortedData.length : Number(newPageSize);
      setCurrentPageSize(size);
      setPage(1);
    }
  };

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
    setPage(1);
  };

  const toggleFilterPopover = (columnKey: string) => {
    setFilterPopovers(prev => ({
      ...prev,
      [columnKey]: !prev[columnKey]
    }));
  };

  const renderColumnHeader = (col: ColumnDef<T>) => {
    const columnKey = col.accessor as string;
    const hasActiveFilter = col.filter && col.filter.value.length > 0;
    const headerLabel = typeof col.header === 'string' ? col.header : '';
    const filterLabel = headerLabel
      ? `${getTranslation("LABEL_SORTABLE_TABLE_FILTER") || "Filter"} ${headerLabel}`
      : (getTranslation("LABEL_SORTABLE_TABLE_FILTER") || "Filter");

    return (
      <div className={classes.headerContent}>
        {col.sortable ? (
          <UnstyledButton onClick={() => handleSort(columnKey)} className={classes.headerButton}>
            <Text fw={600} fz="sm" className={classes.headerText}>
              {col.header}
            </Text>
            {sortColumn === col.accessor ? (
              sortDirection === 'asc' ? (
                <IconChevronUp size={16} color="#009DF0" />
              ) : (
                <IconChevronDown size={16} color="#009DF0" />
              )
            ) : (
              <IconSelector size={16} color="#009DF0" />
            )}
          </UnstyledButton>
        ) : (
          <Text fw={600} fz="sm" className={classes.headerText}>
            {col.header}
          </Text>
        )}
        
        {col.filter && (
          <Popover
            opened={filterPopovers[columnKey] || false}
            onChange={(opened) => setFilterPopovers(prev => ({ ...prev, [columnKey]: opened }))}
            position="bottom"
            withArrow
            shadow="md"
          >
            <Popover.Target>
              <ActionIcon
                variant={hasActiveFilter ? 'filled' : 'subtle'}
                color={hasActiveFilter ? 'blue' : 'gray'}
                onClick={() => toggleFilterPopover(columnKey)}
                size="sm"
                aria-label={filterLabel}
              >
                <IconFilter size={14} />
              </ActionIcon>
            </Popover.Target>
            <Popover.Dropdown>
              {col.filter.type === 'multiselect' && (
                <MultiSelect
                  value={col.filter.value}
                  onChange={col.filter.onChange}
                  data={col.filter.options ? col.filter.options(data) : []}
                  placeholder={
                    col.filter.placeholder ||
                    `${filterLabel}${filterLabel.endsWith("...") ? "" : "..."}`
                  }
                  size="xs"
                  searchable
                  clearable
                  withCheckIcon
                  hidePickedOptions={false}
                  maxDropdownHeight={200}
                  style={{ minWidth: 200 }}
                />
              )}
            </Popover.Dropdown>
          </Popover>
        )}
      </div>
    );
  };

  let sortedData = [...data];
  if (sortColumn) {
    sortedData.sort((a, b) => {
      const aValue = getValueByPath(a, sortColumn as string);
      const bValue = getValueByPath(b, sortColumn as string);
      
      const aIsEmpty = aValue == null || aValue === '';
      const bIsEmpty = bValue == null || bValue === '';
      
      if (aIsEmpty && bIsEmpty) return 0;
      if (aIsEmpty) return 1; 
      if (bIsEmpty) return -1; 
      
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortDirection === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      }
      return 0;
    });
  }

  const numPages = Math.ceil(sortedData.length / currentPageSize);
  const pagedData = showPagination ? sortedData.slice((activePage - 1) * currentPageSize, activePage * currentPageSize) : sortedData;
  
  const startIndex = (activePage - 1) * currentPageSize + 1;
  const endIndex = Math.min(activePage * currentPageSize, sortedData.length);

  return (
    <Box className={classes.tableContainer}>
      {/* Fixed Pagination Bar - outside the scrollable area */}
      {showPagination && (
        <div className={classes.paginationBar}>
          <Group justify="space-between" align="center" w="100%">
            {/* Left side - Page size selector and info */}
            <Group align="center" gap="md">
              {/* Page Size Selector */}
              <Group align="center" gap={4}>
                <Text size="xs" c="dimmed">{getTranslation("LABEL_SORTABLE_TABLE_SHOW") || "Show:"}</Text>
                <Select
                  value={currentPageSize === sortedData.length ? 'all' : currentPageSize.toString()}
                  onChange={handlePageSizeChange}
                  data={[
                    ...pageSizeOptions.map(size => ({ value: size.toString(), label: size.toString() })),
                    { value: 'all', label: getTranslation("LABEL_SORTABLE_TABLE_ALL") || "All" }
                  ]}
                  size="xs"
                  w={70}
                  comboboxProps={{ withinPortal: false }}
                />
              </Group>
              
              {/* Info Display */}
              <Text size="sm" c="dimmed">
                {(getTranslation("LABEL_SORTABLE_TABLE_SHOWING") || "Showing")} {startIndex}-{endIndex} {(getTranslation("LABEL_SORTABLE_TABLE_OF") || "of")} {sortedData.length}
              </Text>
              {title && (
                <>
                  <Text size="sm" c="dimmed">•</Text>
                  <Text size="sm" fw={500} c="dark">
                    {title}
                  </Text>
                </>
              )}
            </Group>
            
            {/* Right side - Page navigation */}
            <Group align="center" gap="md">
              {numPages > 1 && (
                <Pagination 
                  value={activePage} 
                  onChange={setPage} 
                  total={numPages}
                  size="xs"
                  siblings={1}
                  boundaries={1}
                />
              )}
            </Group>
          </Group>
        </div>
      )}

      {/* Scrollable table container */}
      <div className={classes.scrollContainer}>
        <Table 
          striped 
          highlightOnHover 
          withRowBorders={false}
          withTableBorder={false}
          className={classes.table}
        >
          {/* Fixed Header */}
          <Table.Thead className={classes.tableHeader}>
            {/* Column Headers Row */}
            <Table.Tr>
              {columns.map((col) => (
                <Table.Th
                  key={col.accessor as string}
                  className={classes.headerCell}
                  style={{ width: col.width }}
                >
                  {renderColumnHeader(col)}
                </Table.Th>
              ))}
              {actionColumns && actionColumns.map((action, idx) => (
                <Table.Th 
                  key={action.label || idx} 
                  className={`${classes.headerCell} ${classes.centerAlign}`}
                  style={{ width: action.width }}
                >
                  <Text fw={500} fz="sm">
                    {action.label || ''}
                  </Text>
                </Table.Th>
              ))}
            </Table.Tr>
            {filterRow}
          </Table.Thead>
          
          {/* Scrollable Body */}
          <Table.Tbody>
            {pagedData.map((row) => (
              <MemoizedTableRow
                key={rowKey(row)}
                row={row}
                rowKey={rowKey(row)}
                columns={columns}
                actionColumns={actionColumns}
                rowStyle={rowStyle ? rowStyle(row) : undefined}
                getValueByPath={getValueByPath}
              />
            ))}
          </Table.Tbody>
        </Table>
      </div>
    </Box>
  );
}
